import React, { lazy, Suspense } from 'react';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

import { COMPOSED_CLAIMS } from 'constants/composedClaims';
import { getIsNativePlatform } from 'utils/platform';

import { selectIsClaimAllowed } from 'store/auth/selectors';

import NativeTelephonyProvider from './NativeTelephonyProvider';

// The web Telnyx WebRTC stack (TelnyxProvider + TelnyxConnectionLogger) statically
// imports @telnyx/webrtc + @telnyx/react-client. Loading it lazily puts those heavy
// SDKs in a separate chunk that is fetched ONLY when WebTelephonyProvider actually
// mounts — which never happens on native (see the getIsNativePlatform gate below),
// so the native app never fetches or executes the web WebRTC SDK.
const WebTelephonyProvider = lazy(() => import('./WebTelephonyProvider'));

/**
 * Always-mounted, claim-gated host for the telephony call session.
 *
 * On web the call session is the Telnyx WebRTC client, mounted once near the top
 * of the layout (instead of inside the TelephonyWidget popover) so the socket and
 * RemoteAudio element survive the widget popover closing or the Header
 * re-rendering, keeping an active call alive across those events.
 *
 * On NATIVE the web WebRTC stack must be fully absent: the native NativeCalling
 * plugin owns the call session, and mounting the web provider too would open a
 * SECOND Telnyx registration (double-ring + connection conflict). So when
 * getIsNativePlatform() is true we render children WITHOUT the web provider — and
 * because the provider module is lazily imported, the @telnyx/webrtc chunk is
 * never even fetched on native.
 *
 * When the telephony claim is not allowed, children render without the provider
 * so no socket is opened.
 */
const TelephonyHost = ({ children }) => {
  const isTelephonyEnabled = useSelector(selectIsClaimAllowed(COMPOSED_CLAIMS.TELEPHONY_ENABLED));

  if (!isTelephonyEnabled) {
    return children;
  }

  // Native: the NativeCalling plugin owns the session — log it in + bind its events to Redux (no web
  // WebRTC stack, so the @telnyx/webrtc chunk is never fetched).
  if (getIsNativePlatform()) {
    return <NativeTelephonyProvider>{children}</NativeTelephonyProvider>;
  }

  return (
    <Suspense fallback={children}>
      <WebTelephonyProvider>{children}</WebTelephonyProvider>
    </Suspense>
  );
};

TelephonyHost.propTypes = {
  children: PropTypes.node.isRequired,
};

export default TelephonyHost;
